import { SshMessageWriter } from '../protocol/message-writer.js';

/**
 * The set of session keys derived after key exchange.
 */
export type SessionKeys = {
	/** IV for client-to-server encryption */
	ivClientToServer: Uint8Array;
	/** IV for server-to-client encryption */
	ivServerToClient: Uint8Array;
	/** Encryption key for client-to-server */
	encKeyClientToServer: Uint8Array;
	/** Encryption key for server-to-client */
	encKeyServerToClient: Uint8Array;
	/** Integrity key for client-to-server (unused with GCM) */
	macKeyClientToServer: Uint8Array;
	/** Integrity key for server-to-client (unused with GCM) */
	macKeyServerToClient: Uint8Array;
};

/**
 * Derive a single key using the SSH key derivation function (RFC 4253 §7.2).
 *
 * K1 = HASH(K || H || X || session_id)
 * K2 = HASH(K || H || K1)
 * K3 = HASH(K || H || K1 || K2)
 * key = K1 || K2 || K3 || ...
 *
 * @param sharedSecret K as bigint
 * @param exchangeHash H
 * @param letter X — one of "A".."F"
 * @param sessionId The session identifier (H from first key exchange)
 * @param neededBytes How many bytes of key material to produce
 * @param hashAlgorithm Hash algorithm name (e.g. "SHA-256")
 */
async function deriveKey(
	sharedSecret: bigint,
	exchangeHash: Uint8Array,
	letter: string,
	sessionId: Uint8Array,
	neededBytes: number,
	hashAlgorithm: string,
): Promise<Uint8Array> {
	// build the initial hash input: K (mpint) || H || letter || session_id
	const w = new SshMessageWriter();
	w.writeMpint(sharedSecret);
	w.writeRawBytes(exchangeHash);
	w.writeByte(letter.charCodeAt(0));
	w.writeRawBytes(sessionId);

	const k1Buf = await crypto.subtle.digest(hashAlgorithm, new Uint8Array(w.toUint8Array()));
	let key = new Uint8Array(k1Buf);

	// if we need more bytes, extend
	while (key.length < neededBytes) {
		const extW = new SshMessageWriter();
		extW.writeMpint(sharedSecret);
		extW.writeRawBytes(exchangeHash);
		extW.writeRawBytes(key);

		const nextBuf = await crypto.subtle.digest(hashAlgorithm, new Uint8Array(extW.toUint8Array()));
		const next = new Uint8Array(nextBuf);

		const combined = new Uint8Array(key.length + next.length);
		combined.set(key);
		combined.set(next, key.length);
		key = combined;
	}

	// truncate to exact size
	return key.subarray(0, neededBytes);
}

/**
 * Derive all session keys from the key exchange results.
 *
 * @param sharedSecret K from key exchange
 * @param exchangeHash H from key exchange
 * @param sessionId Session ID (= H from the *first* key exchange)
 * @param encKeyLength Encryption key length in bytes (e.g. 16 for AES-128)
 * @param ivLength IV length in bytes (e.g. 12 for GCM)
 * @param macKeyLength MAC key length in bytes (e.g. 32 for HMAC-SHA-256, unused with GCM)
 * @param hashAlgorithm Hash algorithm (e.g. "SHA-256" for curve25519-sha256)
 */
export async function deriveSessionKeys(
	sharedSecret: bigint,
	exchangeHash: Uint8Array,
	sessionId: Uint8Array,
	encKeyLength: number,
	ivLength: number,
	macKeyLength: number,
	hashAlgorithm: string = 'SHA-256',
): Promise<SessionKeys> {
	const [ivC2S, ivS2C, encC2S, encS2C, macC2S, macS2C] = await Promise.all([
		deriveKey(sharedSecret, exchangeHash, 'A', sessionId, ivLength, hashAlgorithm),
		deriveKey(sharedSecret, exchangeHash, 'B', sessionId, ivLength, hashAlgorithm),
		deriveKey(sharedSecret, exchangeHash, 'C', sessionId, encKeyLength, hashAlgorithm),
		deriveKey(sharedSecret, exchangeHash, 'D', sessionId, encKeyLength, hashAlgorithm),
		deriveKey(sharedSecret, exchangeHash, 'E', sessionId, macKeyLength, hashAlgorithm),
		deriveKey(sharedSecret, exchangeHash, 'F', sessionId, macKeyLength, hashAlgorithm),
	]);

	return {
		ivClientToServer: ivC2S,
		ivServerToClient: ivS2C,
		encKeyClientToServer: encC2S,
		encKeyServerToClient: encS2C,
		macKeyClientToServer: macC2S,
		macKeyServerToClient: macS2C,
	};
}
